/**
 * The permission lookups a signed-in session needs as the app boots: the
 * caller's effective permission keys and their role.
 *
 * Moved verbatim out of permissionService.ts, which re-exports all of them.
 * PermissionsProvider is mounted on every page, public ones included, so what
 * it imports ships in the entry chunk; importing permissionService for these
 * four functions carried the whole role and permission administration module
 * (CRUD, the permission matrix, audit logs) to every visitor.
 */

import { supabase } from "./supabase";
import { memoizeBriefly } from "@/utils/memoizeBriefly";
import { registerIdentityCache } from "@/utils/identityCacheRegistry";
import type { Permission, Role } from "@/types/database.types";
import type { PermissionServiceResponse } from "./permissionService";

/**
 * Get all permissions
 */
export async function getAllPermissions(): Promise<
  PermissionServiceResponse<Permission[]>
> {
  try {
    const { data, error } = await supabase
      .from("es_permissions")
      .select("*")
      .order("category")
      .order("display_order");

    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    console.error("Error fetching permissions:", err);
    return { data: null, error: "Failed to fetch permissions" };
  }
}

/**
 * Get permission keys for a role (for quick checking)
 */
export async function getRolePermissionKeys(
  roleId: string,
): Promise<PermissionServiceResponse<string[]>> {
  try {
    const { data, error } = await supabase
      .from("es_role_permissions")
      .select(
        `
        es_permissions!inner (permission_key)
      `,
      )
      .eq("role_id", roleId);

    if (error) throw error;

    const keys =
      data
        ?.map((rp: any) => {
          // Handle both single object and array responses from Supabase
          const perm = Array.isArray(rp.es_permissions)
            ? rp.es_permissions[0]
            : rp.es_permissions;
          return perm?.permission_key;
        })
        .filter(Boolean) || [];
    return { data: keys, error: null };
  } catch (err) {
    console.error("Error fetching role permission keys:", err);
    return { data: null, error: "Failed to fetch role permission keys" };
  }
}

/**
 * Get a user's role with hierarchy info
 * Uses separate queries to avoid embedded FK hint issues with RLS
 */
export async function getUserRole(
  userId: string,
): Promise<PermissionServiceResponse<Role & { hierarchy_level: number }>> {
  try {
    // Step 1: Get user's role_id
    const { data: userData, error: userError } = await supabase
      .from("es_users")
      .select("role_id")
      .eq("id", userId)
      .maybeSingle();

    if (userError) throw userError;

    if (!userData?.role_id) {
      return { data: null, error: "User has no role assigned" };
    }

    // Step 2: Get role details separately
    const { data: roleData, error: roleError } = await supabase
      .from("es_roles")
      .select("*")
      .eq("id", userData.role_id)
      .single();

    if (roleError) throw roleError;

    if (!roleData) {
      return { data: null, error: "Role not found" };
    }

    return {
      data: {
        ...roleData,
        hierarchy_level: roleData.hierarchy_level || 0,
      } as Role & { hierarchy_level: number },
      error: null,
    };
  } catch (err) {
    console.error("Error fetching user role:", err);
    return { data: null, error: "Failed to fetch user role" };
  }
}

/**
 * Get all permission keys for a user
 */
async function getUserPermissionKeysUncached(
  userId: string,
): Promise<PermissionServiceResponse<string[]>> {
  try {
    // First get the user's role_id
    // Use maybeSingle() to avoid 406 error when user doesn't exist yet (during signup)
    const { data: userData, error: userError } = await supabase
      .from("es_users")
      .select("role_id")
      .eq("id", userId)
      .maybeSingle();

    // If user doesn't exist yet (race condition during signup), return empty permissions
    if (userError || !userData) {
      if (!userData) {
        console.warn(
          `[PermissionService] User ${userId} not found in es_users (may be during signup), returning empty permissions`,
        );
      }
      return { data: [], error: null };
    }

    // Effective keys: the union over the title role and every active duty
    // role, computed by public.user_permission_keys() — the same function the
    // edge-function staff resolver and the RLS helper read, so the three can
    // no longer disagree. The role walk below stays as the fallback so a
    // client deployed ahead of the migration keeps working on title-role keys.
    const { data: effectiveRows, error: effectiveError } = await supabase.rpc(
      "user_permission_keys",
      { p_user_id: userId },
    );

    if (!effectiveError && Array.isArray(effectiveRows)) {
      const keys = (effectiveRows as Array<{ permission_key?: unknown }>)
        .map((row) => row?.permission_key)
        .filter(
          (key): key is string => typeof key === "string" && key.length > 0,
        );
      return { data: keys, error: null };
    }

    if (effectiveError) {
      console.warn(
        "[PermissionService] user_permission_keys unavailable, falling back to the title role:",
        effectiveError.message,
      );
    }

    if (!userData?.role_id) {
      return { data: [], error: null };
    }

    // Get role info including hierarchy
    const { data: roleData, error: roleError } = await supabase
      .from("es_roles")
      .select("hierarchy_level, is_system_role, name")
      .eq("id", userData.role_id)
      .single();

    if (roleError) throw roleError;

    // Super Admin has all permissions
    if (roleData?.hierarchy_level === 100 || roleData?.name === "Super Admin") {
      const { data: allPerms } = await getAllPermissions();
      return {
        data: allPerms?.map((p) => p.permission_key) || [],
        error: null,
      };
    }

    // Get permission keys for the role
    return await getRolePermissionKeys(userData.role_id);
  } catch (err) {
    console.error("Error fetching user permission keys:", err);
    return { data: null, error: "Failed to fetch user permissions" };
  }
}

/**
 * Effective permission keys for a user, memoised for a few seconds.
 *
 * A single application list load asked this question up to four times for the
 * same user: once at session start and once inside each of the queue queries
 * running concurrently. Each answer costs an es_users read plus the
 * user_permission_keys RPC.
 *
 * An empty result is deliberately NOT cached. A freshly provisioned user can
 * legitimately return zero keys for a moment while the database trigger that
 * creates their es_users row settles, and PermissionsProvider retries on a
 * 500ms / 1s / 2s backoff to ride that out. Caching the empty answer would
 * hand every one of those retries the same nothing and strand the user on an
 * access-denied screen.
 */
export const getUserPermissionKeys = memoizeBriefly(
  getUserPermissionKeysUncached,
  {
    ttlMs: 5000,
    key: (userId: string) => userId,
    cacheable: (result) =>
      result.error === null && (result.data?.length ?? 0) > 0,
  },
);

/** Drop memoised permission keys. Call when the signed-in user may have changed. */
export function invalidateUserPermissionKeys(): void {
  getUserPermissionKeys.invalidate();
}

registerIdentityCache(invalidateUserPermissionKeys);
